import { useState } from "react";
import { Phone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CLARITY_EVENTS, clarityEvent } from "@/lib/clarity";
import { pixelLead } from "@/lib/pixel";
import { isWithinBusinessHours } from "@/lib/businessHours";
import {
  BUSINESS_HOURS,
  WHATSAPP_DEFAULT_MESSAGE,
  WHATSAPP_NUMBER,
} from "@/config/contact";
import CallbackForm from "@/components/contact/CallbackForm";

/** Canal de contacto flotante del sitio: WhatsApp medido y solicitud de llamada. */
export const WhatsAppFloat = () => {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const available = isWithinBusinessHours();

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    WHATSAPP_DEFAULT_MESSAGE
  )}`;

  const handleWhatsAppClick = () => {
    pixelLead();
    clarityEvent(CLARITY_EVENTS.CONTACT_WHATSAPP_WIDGET);
  };

  const hoursText = `${BUSINESS_HOURS.startHour}:00 a ${BUSINESS_HOURS.endHour}:00`;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {available !== null && (
        <span
          aria-live="polite"
          className={`rounded-full px-3 py-1 text-xs font-medium shadow-sm ${
            available
              ? "bg-green-100 text-green-800"
              : "bg-amber-100 text-amber-800"
          }`}
        >
          {available ? "Disponible ahora" : "Fuera de horario"}
        </span>
      )}

      {available === false && (
        <span className="max-w-[15rem] rounded-xl bg-white/95 px-3 py-2 text-right text-xs text-brown-light shadow-sm">
          Te atendemos de {hoursText} (hora de Panamá). Dejanos tu pedido y te
          respondemos al abrir.
        </span>
      )}

      <Dialog open={isCallbackOpen} onOpenChange={setIsCallbackOpen}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full border-primary/40 bg-white/95 shadow-sm"
          >
            <Phone className="mr-1 h-4 w-4" />
            Solicitar llamada
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-playfair">
              Solicitar una llamada
            </DialogTitle>
            <DialogDescription>
              Dejanos tu nombre y teléfono, y te llamamos en la franja que
              elijas.
            </DialogDescription>
          </DialogHeader>
          <CallbackForm onSuccess={() => setIsCallbackOpen(false)} />
        </DialogContent>
      </Dialog>

      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Contáctanos por WhatsApp"
        onClick={handleWhatsAppClick}
        className="group relative flex h-14 w-14 items-center justify-center rounded-full border-2 border-primary/60 bg-brown shadow-lg transition-all duration-300 hover:scale-110 hover:bg-brown-dark hover:shadow-xl"
      >
        <span className="absolute inset-0 rounded-full bg-primary/40 animate-pulse-ring" />

        <svg className="relative z-10 h-7 w-7" fill="white" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
      </a>
    </div>
  );
};